import { cn } from "@/lib/utils";
import { AdaptiveText, type AdaptiveTextProps, Title } from "./Content";
import { Sizes } from "./Content/styles";
import type { IconName } from "./Icon";
import ItemIcon from "./ItemIcon";
import { StaggerContainer, StaggerItem } from "./animations/page-transitions";

export type FeatureProps = {
	features: {
		name: string;
		description: AdaptiveTextProps["portableText"];
		icon?: IconName;
		iconUrl?: string;
		isImage?: boolean;
	}[];
};

const List = ({ features }: FeatureProps) => {
	return (
		<StaggerContainer>
			<dl className="mt-10 max-w-xl space-y-6 text-base leading-7 lg:max-w-none">
				{features.map((feature) => (
					<StaggerItem key={feature.name}>
						<div className="relative pl-16">
							<dt className="inline">
								<ItemIcon
									icon={feature.icon}
									iconUrl={feature.iconUrl}
									alt={feature.name}
									isImage={feature.isImage}
									width={128}
									height={128}
									className="absolute top-1 left-1 flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30"
								/>
								<Title as="h3" className="text-sm sm:text-base md:text-base">
									{feature.name}
								</Title>
							</dt>
							<AdaptiveText
								portableText={feature.description}
								size={Sizes.Card}
								className="mt-1"
							/>
						</div>
					</StaggerItem>
				))}
			</dl>
		</StaggerContainer>
	);
};

const Grid = ({
	features,
	threeColumns = true,
}: FeatureProps & {
	threeColumns?: boolean;
}) => {
	return (
		<div className="mx-auto mt-16 max-w-7xl sm:mt-20 md:mt-24">
			<dl
				className={cn(
					"mx-auto grid max-w-7xl grid-cols-1 gap-x-10 gap-y-10 text-base leading-7 sm:grid-cols-2 lg:gap-x-20 lg:gap-y-12",
					{
						"lg:mx-0 lg:grid-cols-3": threeColumns,
					},
				)}
			>
				{features.map((feature) => (
					<StaggerItem key={feature.name}>
						<div className="relative pl-16">
							<dt className="inline font-semibold text-sm md:text-base">
								<ItemIcon
									icon={feature.icon}
									iconUrl={feature.iconUrl}
									alt={feature.name}
									isImage={feature.isImage}
									width={128}
									height={128}
									className="absolute top-1 left-1 flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary shadow-xs ring-1 ring-primary/30"
								/>
								{feature.name}
							</dt>
							<AdaptiveText
								portableText={feature.description}
								className="mt-0 text-sm md:text-base"
							/>
						</div>
					</StaggerItem>
				))}
			</dl>
		</div>
	);
};

const Features = {
	List,
	Grid,
};

export default Features;
